import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import SearchIcon from '@tamagoshi/icons/Search';
import ArrowBack from '@tamagoshi/icons/ArrowBack';
import cn from 'classnames';
import Button from '../Button';
import InputClose from './InputClose';

import styles from './styles/index.module.scss';

/**
 * Id's described in this component are required by searchbar script from manomano-sf
 * i.e => submit-form-frame, submit-form, searchbar-basic-icon
 */

const getClassName = (id, color) =>
  cn({
    [styles[`${id}`]]: true,
    [styles[`${id}Primary`]]: color === 'primary',
    [styles[`${id}Transparent`]]: color === 'transparent',
    [styles[`${id}Secondary`]]: color === 'secondary',
  });

const Input = ({
  value,
  color,
  closeIconId,
  className,
  suffix,
  placeholder,
  onChange,
  ...restProps
}) => {
  const [isSearchIconDisplayed, setDisplaySearchIcon] = useState(true);

  const handleInputFocus = useCallback(() => {
    setDisplaySearchIcon(false);
  }, []);

  const leaveNavigation = useCallback(() => {
    window.manomano.header.hideSearchBarResult();
    setDisplaySearchIcon(true);
  }, []);

  const removeInputText = useCallback(() => {
    onChange('');
    window.manomano.header.insertHistory();
  }, []);

  const handleKeyDown = useCallback(event => {
    if (event.which === 27) {
      removeInputText();
    }
  }, []);

  const inputClassName = cn(getClassName('input', color), className);
  const inputButtonClassName = getClassName('inputButton', color);
  const onChangeInput = event => onChange(event.target.value);

  return (
    <div className={styles.inputContainer}>
      <div className={inputClassName}>
        <input
          {...restProps}
          value={value}
          className={styles.inputControl}
          placeholder={placeholder}
          onChange={onChangeInput}
          onFocus={handleInputFocus}
        />
        <span id="searchbar-basic-icon">
          {isSearchIconDisplayed ? (
            <SearchIcon className={styles.searchIcon} />
          ) : (
            <ArrowBack
              className={styles.backArrowIcon}
              onClick={leaveNavigation}
            />
          )}
        </span>

        <InputClose
          value={value}
          closeIconId={closeIconId}
          onClick={removeInputText}
          onKeyDown={handleKeyDown}
        />
      </div>
      <Button
        type="icon"
        id="submit-form"
        data-qa="searchButtonCta"
        className={inputButtonClassName}
      >
        {suffix}
      </Button>
    </div>
  );
};

Input.propTypes = {
  value: PropTypes.string,
  color: PropTypes.oneOf(['primary', 'secondary', 'transparent']),
  className: PropTypes.string,
  placeholder: PropTypes.string.isRequired,
  closeIconId: PropTypes.string,
  suffix: PropTypes.node,
  onChange: PropTypes.func,
};

Input.defaultProps = {
  value: '',
  color: 'primary',
  className: undefined,
  suffix: undefined,
  closeIconId: undefined,
  onChange: () => {},
};

export default Input;
